import { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Paper, Typography, Button, Alert, MuiTextField, CircularProgress } from '@/ui';
import { httpGet } from '@/shared/api/http';
import { uwAdminEndpoints } from '@/shared/api/uwEndpoints';

// Must match ADMIN_KEY_STORAGE_KEY in shared/api/http.ts — that interceptor
// reads the same sessionStorage entry to attach the X-Admin-Key header.
const ADMIN_KEY_STORAGE_KEY = 'uwAdminKey';

// ── Domain allow-list ────────────────────────────────────
// The admin debug console is available on dev/UAT, local dev, AND production
// (hpa.utilitiesplus.co.uk) — production access was enabled by request. The
// backend (requireAdminKey + UW_ADMIN_ALLOWED_ORIGINS) enforces the same list,
// so editing this client-side only hides UI from a request that would be 403'd
// anyway. localhost / 127.0.0.1 cover any local dev port. NOTE: access is gated
// by the admin key ALONE (no login), so keep the key a strong per-env secret.
const ALLOWED_ADMIN_HOSTNAMES = new Set<string>([
  'uw-dev.iehub.co.uk',
  'partner-uat.utilitiesplus.co.uk',
  'hpa.utilitiesplus.co.uk',
  'localhost',
  '127.0.0.1',
]);

const isAdminHostAllowed = (): boolean => {
  try {
    return ALLOWED_ADMIN_HOSTNAMES.has(window.location.hostname);
  } catch {
    return false;
  }
};

const readStoredKey = (): string | null => {
  try {
    return sessionStorage.getItem(ADMIN_KEY_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredKey = (value: string): void => {
  try {
    sessionStorage.setItem(ADMIN_KEY_STORAGE_KEY, value);
  } catch {
    /* ignore blocked storage */
  }
};

const clearStoredKey = (): void => {
  try {
    sessionStorage.removeItem(ADMIN_KEY_STORAGE_KEY);
  } catch {
    /* ignore blocked storage */
  }
};

type GateState = 'checking' | 'blocked' | 'locked' | 'unlocked';

/**
 * Secret-key gate for the admin/debug log viewers (webhook logs + API
 * failures). The entered key is held in sessionStorage and validated against
 * the backend (which enforces it on every admin endpoint — this gate only
 * hides the UI from anyone who would be 403'd anyway). sessionStorage keeps the
 * key per-tab and clears it when the tab closes.
 */
export default function RequireAdminKey(): JSX.Element {
  const [state, setState] = useState<GateState>('checking');
  const [keyInput, setKeyInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Verifies whatever key is currently in sessionStorage (the interceptor
  // attaches it). Resolves true only on a 2xx from the verify endpoint.
  const verifyStoredKey = useCallback(async (): Promise<boolean> => {
    try {
      await httpGet(uwAdminEndpoints.verifyKey());
      return true;
    } catch {
      return false;
    }
  }, []);

  // On mount, silently re-validate an already-stored key so an admin who
  // entered it earlier this session isn't prompted again.
  useEffect(() => {
    let active = true;
    // Domain gate first — never probe the key (or even render the prompt) off an
    // unapproved host such as production.
    if (!isAdminHostAllowed()) {
      setState('blocked');
      return;
    }
    if (!readStoredKey()) {
      setState('locked');
      return;
    }
    void verifyStoredKey().then((ok) => {
      if (!active) return;
      if (ok) {
        setState('unlocked');
      } else {
        clearStoredKey();
        setState('locked');
      }
    });
    return () => {
      active = false;
    };
  }, [verifyStoredKey]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = keyInput.trim();
      if (!trimmed) {
        setError('Enter the admin key.');
        return;
      }
      setSubmitting(true);
      setError(null);
      // Store first so the interceptor attaches it to the verify request.
      writeStoredKey(trimmed);
      const ok = await verifyStoredKey();
      if (ok) {
        setKeyInput('');
        setState('unlocked');
      } else {
        clearStoredKey();
        setError('That key was not accepted.');
      }
      setSubmitting(false);
    },
    [keyInput, verifyStoredKey]
  );

  if (state === 'checking') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state === 'blocked') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', px: 2 }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 420, width: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Not available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The admin debug console is not available on this domain.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (state === 'unlocked') {
    return <Outlet />;
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', px: 2 }}>
      <Paper
        elevation={3}
        component="form"
        onSubmit={handleSubmit}
        sx={{ p: 4, maxWidth: 420, width: '100%' }}
      >
        <Typography variant="h6" gutterBottom>
          Admin access required
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter the admin key to view the debug log console. This area exposes
          sensitive request/response data, so access is restricted.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <MuiTextField
          type="password"
          label="Admin key"
          value={keyInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyInput(e.target.value)}
          fullWidth
          autoFocus
          autoComplete="off"
          disabled={submitting}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          loading={submitting}
          loadingText="Checking…"
          sx={{ mt: 2 }}
        >
          Unlock
        </Button>
      </Paper>
    </Box>
  );
}
